import type { FC } from "preact/compat";
import type { ProgressProps } from "./interfaces";
import { useSignal } from "@preact/signals";
import cn from "classnames";

const Progress: FC<ProgressProps> = ({
    id,
    readOnly,
    value = 0,
    min = 0,
    max = 100,
    step = 5,
    onChange,
    ticks = Boolean(onChange),
    renderLabel,
    height = "2rem",
}) => {
    const getPercent = (val: number) => 100 * ((val - min) / (max - min));

    const isFocused = useSignal(false);
    const percent = getPercent(value);
    const clamped = Math.min(Math.max(0, percent), 100);
    const ticksCount = 1 + Math.floor((max - min) / step);

    const tickLabels = Array.from(
        { length: ticksCount },
        (_, idx) => idx * step
    ).filter((tick) => tick !== value);

    if (!renderLabel) {
        renderLabel = () => <>{Math.round(percent)}%</>;
    }

    return (
        <label
            style={{ height }}
            className={cn(
                "relative flex items-center w-full bg-gray-100 shadow-md rounded-[20px]",
                isFocused.value && "ring ring-sky-500"
            )}
        >
            <input
                id={id}
                type="range"
                className="absolute w-0 h-0 opacity-0"
                readOnly={readOnly}
                disabled={readOnly}
                min={min}
                max={max}
                step={step}
                value={value}
                onInput={(event) => onChange?.(+event.currentTarget.value)}
                onFocus={() => (isFocused.value = true)}
                onBlur={() => (isFocused.value = false)}
            />
            <span
                style={{ width: `${clamped}%` }}
                className="absolute top-0 left-0 h-full rounded-[20px] bg-gradient-to-r from-[#0196E1] to-[#F31B94] z-[2] transition-[width] duration-300"
            />
            <span
                style={{
                    right: `${100 - clamped}%`,
                }}
                className={cn(
                    "absolute px-1 text-xs font-bold z-[4] transition-[right] duration-300",
                    value === min ? "text-sky-800" : "text-white"
                )}
            >
                {renderLabel(value)}
            </span>
            {ticks &&
                tickLabels.map((tick, idx) => (
                    <div
                        key={tick}
                        style={{
                            left: `${getPercent(tick)}%`,
                        }}
                        onClick={(event) => {
                            event.preventDefault();
                            onChange?.(tick);
                        }}
                        className="absolute z-[3] flex flex-col items-center justify-center p-2 -translate-x-1/2 cursor-pointer group"
                    >
                        <span
                            className={cn(
                                "block text-gray-400 text-[10px] group-hover:text-sky-600 absolute -mt-10",
                                idx === tickLabels.length - 1 && "mr-4"
                            )}
                        >
                            {Math.floor(getPercent(tick))}
                        </span>
                        <span
                            className={cn(
                                "block w-1 h-1 transition duration-100 rounded-full bg-sky-300 group-hover:scale-[2]",
                                idx === tickLabels.length - 1 && "mr-4",
                                tick < value
                                    ? "group-hover:bg-white"
                                    : "group-hover:bg-sky-600"
                            )}
                        />
                    </div>
                ))}
        </label>
    );
};

export default Progress;
